<script lang="ts" setup>
import ChevronLeft from '~/assets/icon/navigation/Chevron/Left.svg'
import ChevronRight from '~/assets/icon/navigation/Chevron/Right.svg'

const props = defineProps({
  modelValue: {
    type: Number,
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  count: {
    type: Number,
    required: true,
  },
  pageRange: {
    type: Number,
    default: 3,
  },
})
const emit = defineEmits(['update:modelValue'])

const pageSelected = computed(() => {
  return props.modelValue
})

const totalPages = computed(() => {
  return Math.ceil(props.count / props.pageSize)
})

const pages = computed(() => {
  const items = {}
  // If there are fewer items than the allowed range, display pages only
  if (totalPages.value <= props.pageRange) {
    for (let i = 0; i < totalPages.value; i++) {
      items[i] = { index: i, content: i + 1 }
    }
    return items
  }
  // Otherwise we must "cut" the pages to show
  const halfPageRange = Math.floor(props.pageRange / 2)

  // First always show the first page
  items[0] = { index: 0, content: 1 }

  // Then Add the range around the selected page
  let low = 0
  if (pageSelected.value - halfPageRange > 0) {
    low = pageSelected.value - 1 - halfPageRange
  }
  let high = low + props.pageRange - 1
  if (high >= totalPages.value) {
    high = totalPages.value - 1
    low = high - props.pageRange + 1
  }
  for (let i = low; i <= high && i <= totalPages.value - 1; i++) {
    items[i] = { index: i, content: i + 1 }
  }

  // Check if there is breakView in the left of selected range
  if (low > 1) {
    items[low - 1] = { index: low - 1, content: '...', break: true, add: -3 }
  }
  // Check if there is breakView in the right of selected range
  if (high + 1 < totalPages.value - 1) {
    items[high + 1] = { index: high + 1, content: '...', break: true, add: 3 }
  }

  // Finaly always show the last page
  items[totalPages.value - 1] = {
    index: totalPages.value - 1,
    content: totalPages.value,
  }
  return items
})

const select = (i) => {
  if (i === pageSelected.value) return
  emit('update:modelValue', i)
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, 50)
}
const prev = () => {
  if (pageSelected.value <= 1) return
  select(pageSelected.value - 1)
}
const next = () => {
  if (pageSelected.value >= totalPages.value) return
  select(pageSelected.value + 1)
}
</script>
<template>
  <div class="flex gap-0.5 items-center">
    <button
      class="flex justify-center items-center w-10 h-10 disabled:text-shade-300 bg-shade-50 focus:bg-shade-50 border-2 border-transparent focus:border-primary-400 disabled:cursor-not-allowed hover:bg-shade-alpha-300"
      :disabled="modelValue === 1"
      @click="prev"
    >
      <ChevronLeft class="h-6" />
    </button>
    <div v-for="page in pages" :key="page.index">
      <button v-if="page.break" class="p-1.5 font-semibold bg-shade-50">
        <span class="inline-block" @click="select(modelValue + page.add)">
          {{ page.content }}
        </span>
      </button>
      <button
        v-else
        class="p-1.5 font-semibold border-2 border-transparent focus:border-primary-400 cursor-pointer"
        :class="
          page.content == modelValue
            ? 'bg-primary-400 text-shade-50'
            : 'bg-shade-50 hover:bg-shade-alpha-300 focus:bg-shade-50'
        "
        @click="select(page.content)"
      >
        <span class="inline-block min-w-[24px]">{{ page.content }}</span>
      </button>
    </div>
    <button
      class="flex justify-center items-center w-10 h-10 disabled:text-shade-300 bg-shade-50 focus:bg-shade-50 border-2 border-transparent focus:border-primary-400 disabled:cursor-not-allowed hover:bg-shade-alpha-300"
      :disabled="modelValue === totalPages"
      @click="next"
    >
      <ChevronRight class="h-6" />
    </button>
  </div>
</template>
